import { useState } from "react";
import { login, requestInmateAccessReset } from "../api/auth";
import AsyncButton from "../components/AsyncButton";
import StatusMessage from "../components/StatusMessage";
import { consumeSessionNotice, homeForRole, storeUser } from "../utils/session";
import PublicFooter from "../components/PublicFooter";
import PageMetadata from "../components/PageMetadata";

function redirectForRole(user) {
  window.location.href = homeForRole(user.role, user);
}

function persistSession(res) {
  storeUser(res.user);
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [facilityCode, setFacilityCode] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotFacilityCode, setForgotFacilityCode] = useState("");
  const [forgotInmateNumber, setForgotInmateNumber] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionNotice] = useState(() => consumeSessionNotice());

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError("");

    try {
      const res = await login(
        email,
        password,
        useRecoveryCode ? null : mfaCode,
        facilityCode,
        useRecoveryCode ? recoveryCode : null
      );

      if (res.mfa_setup_required) {
        persistSession(res);
        window.location.href = "/mfa/setup";
        return;
      }

      persistSession(res);

      if (res.recovery_code_used) {
        localStorage.setItem(
          "counselvault_security_notice",
          "You signed in using a recovery code. Generate new recovery codes from your security settings."
        );
      }

      if (res.force_password_reset || res.user?.must_reset_password) {
        window.location.href = "/password/reset-required";
        return;
      }

      redirectForRole(res.user);
    } catch (err) {
      const data = err.response?.data || {};

	if (data.mfa_required) {
	  setMfaRequired(true);
	  setUseRecoveryCode(false);
	  setError(data.message || "Enter the verification code sent to your email.");
	  return;
	}
      setError(data.message || "Invalid login");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setForgotSuccess("");

    try {
      const res = await requestInmateAccessReset(forgotFacilityCode, forgotInmateNumber);
      setForgotSuccess(res.data.message || "Reset request submitted.");
    } catch (err) {
      setError(err.response?.data?.message || "Unable to submit reset request.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <PageMetadata title="Sign In | CounselVault" description="Sign in to an authorized CounselVault facility, attorney, or inmate account." path="/login" />
      <div className="login-card">
        <div className="d-flex align-items-center gap-3 mb-4">
          <div className="brand-mark">CV</div>

          <div>
            <h4 className="mb-0">CounselVault</h4>
            <div className="text-muted small">
              Secure privileged legal mail infrastructure
            </div>
          </div>
        </div>

        <div className="mb-4">
          <h2 className="fw-bold mb-2">{forgotMode ? "Request Access Reset" : "Sign In"}</h2>

          <p className="text-muted mb-0">
            {forgotMode
              ? "Inmates can request facility staff to verify identity and issue a temporary PIN."
              : "Protected attorney-client document delivery for correctional facilities."}
          </p>
        </div>

        <StatusMessage type="warning">{error}</StatusMessage>
        <StatusMessage type="info">{sessionNotice}</StatusMessage>

        <StatusMessage type="success">{forgotSuccess}</StatusMessage>

        {forgotMode ? (
          <form onSubmit={handleForgotSubmit}>
            <div className="mb-3">
              <label className="form-label">Facility Code</label>
              <input
                className="form-control form-control-lg text-uppercase"
                value={forgotFacilityCode}
                onChange={(e) => setForgotFacilityCode(e.target.value.toUpperCase())}
                placeholder="Example: BCHC"
                required
              />
            </div>

            <div className="mb-4">
              <label className="form-label">Inmate Number</label>
              <input
                className="form-control form-control-lg"
                value={forgotInmateNumber}
                onChange={(e) => setForgotInmateNumber(e.target.value)}
                required
              />
            </div>

            <AsyncButton className="btn btn-dark btn-lg w-100" busy={loading} busyText="Submitting…">
              Request reset
            </AsyncButton>

            <button
              type="button"
              className="btn btn-link w-100 mt-3"
              onClick={() => {
                setForgotMode(false);
                setError("");
                setForgotSuccess("");
              }}
            >
              Back to sign in
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Email or Inmate Number</label>

              <input
                className="form-control form-control-lg"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={mfaRequired}
                required
              />

              <div className="form-text">
                Staff and attorneys use email. Inmates use inmate number plus facility code below.
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Password / PIN</label>

              <input
                type="password"
                className="form-control form-control-lg"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={mfaRequired}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">
                Facility Code <span className="text-muted small">inmates only</span>
              </label>

              <input
                className="form-control form-control-lg text-uppercase"
                value={facilityCode}
                onChange={(e) => setFacilityCode(e.target.value.toUpperCase())}
                disabled={mfaRequired}
                placeholder="Example: BCHC"
              />
            </div>

            {mfaRequired && (
              <div className="mb-4">
                {!useRecoveryCode ? (
                  <>
                    <label className="form-label">Email Verification Code</label>

                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      className="form-control form-control-lg"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      placeholder="123456"
                      maxLength="6"
                      required
                      autoFocus
                    />

                  </>
                ) : (
                  <>
                    <label className="form-label">Recovery Code</label>

                    <input
                      type="text"
                      autoComplete="one-time-code"
                      className="form-control form-control-lg text-uppercase"
                      value={recoveryCode}
                      onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                      placeholder="ABCDE-FGHIJ"
                      maxLength="11"
                      required
                      autoFocus
                    />

                    <div className="form-text">
                      Recovery codes are single-use emergency login codes.
                    </div>

                    <button
                      type="button"
                      className="btn btn-link px-0 mt-2"
                      onClick={() => {
                        setUseRecoveryCode(false);
                        setError("");
                      }}
                    >
                      Use authenticator code instead
                    </button>
                  </>
                )}
              </div>
            )}
            <AsyncButton className="btn btn-dark btn-lg w-100" busy={loading} busyText="Signing in…">
              {mfaRequired ? "Verify and sign in" : "Sign in"}
            </AsyncButton>

            {!mfaRequired && (
              <>
                <div className="text-center mt-3">
                  <a href="/password/forgot">Forgot password?</a>
                </div>

                <button
                  type="button"
                  className="btn btn-link w-100 mt-2"
                  onClick={() => {
                    setForgotMode(true);
                    setError("");
                    setForgotSuccess("");
                  }}
                >
                  Forgot inmate access?
                </button>
              </>
            )}
          </form>
        )}

        <div className="mt-4 pt-4 border-top text-muted small">
          Facility-verified privileged legal mail. Content stays protected.
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
